import React from 'react';
import { Link } from 'react-router-dom';

const About = () => (
  <div className="bg-ehh-cream text-ehh-navy">
    {/* Hero Section - Added faith-based statement per client feedback (Item 3) */}
    <section className="relative min-h-[80vh] flex items-center justify-center text-center overflow-hidden">
      <img 
        src="/assets/images/hero-hands.png" 
        alt="Reaching for hope and unity in Zambian communities" 
        className="absolute inset-0 w-full h-full object-cover" 
      />
      <div className="absolute inset-0 bg-ehh-navy/75"></div>
      <div className="relative z-10 container mx-auto px-6 sm:px-8 md:px-12 max-w-5xl">
        <div className="py-12 sm:py-16 md:py-20">
          <img src="/logo.png" alt="EMA House of Hope Logo" className="mx-auto h-24 sm:h-28 md:h-32 mb-6 sm:mb-8" />
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 text-ehh-cream">
            About EMA House of Hope
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl max-w-4xl mx-auto mb-4 leading-relaxed text-ehh-peach px-4 sm:px-0">
            EMA House of Hope is a faith-based organization dedicated to transforming lives in Zambia.
          </p>
          <p className="text-lg sm:text-xl md:text-2xl max-w-4xl mx-auto mb-8 sm:mb-10 leading-relaxed text-ehh-peach px-4 sm:px-0">
            EMA House of Hope (EHH) is a Non-government organisation registered and operating in Zambia since June 2024. The organisation is community-driven and geared to breaking the cycle of intergenerational poverty and hunger through advocacy for sustainable, ecosystem-level change. Our integrated model addresses the root causes of vulnerability by synchronizing interventions in foundational well-being, economic empowerment, and community health. We stabilise lives by meeting basic human needs, equip them with market-relevant skills and knowledge, and strengthen community systems to foster health, accountability, and sustainable growth.
          </p>
        </div>
      </div>
    </section>

    {/* Vision and Mission - Clean two-column layout */}
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Our Vision</h2>
          <p className="text-lg leading-relaxed">
            Equitable systems that guarantee dignity and thriving community where every family is a pillar of resilience and self-reliance.
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mt-12 mb-8">Our Mission</h2>
          <p className="text-lg leading-relaxed">
            To promote lasting community transformation initiatives which build resilient community systems in health, economic opportunity, and governance.
          </p>
        </div>
        <img 
          src="/assets/images/resilient-community.jpg" 
          alt="Zambian community smiling with hope" 
          className="rounded-2xl shadow-xl w-full object-cover h-96" 
        />
      </div>
    </section>

    {/* Integrated Approach - Alternating layout */}
    <section className="py-24 px-6 bg-ehh-navy/5">
      <div className="container mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <img 
          src="/assets/images/unity-hands.jpg" 
          alt="Community unity and synergistic growth" 
          className="rounded-2xl shadow-xl w-full object-cover h-96 order-2 md:order-1" 
        />
        <div className="order-1 md:order-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center md:text-left">
            Our Approach
          </h2>
          <p className="text-lg mb-8 leading-relaxed">
            We focus on tangible sustainable development. Our "Centre of Excellence" serves as an innovation hub where our three pillars interact synergistically:
          </p>
          <ul className="space-y-4 text-lg list-disc list-inside">
            <li>A youth from our feeding program gains skills at our training centre.</li>
            <li>A graduate of our trades programme receives business advisory through our government liaison services.</li>
            <li>A family stabilised by our counselling unit consistently engages in our economic initiatives.</li>
            <li>A self-reinforcing cycle of growth through skills development improves human resource for sustainability.</li>
          </ul>
        </div>
      </div>
    </section>

    {/* Core Values - Clean grid without external images */}
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl md:text-5xl font-bold text-center mb-16">Our Core Values</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg text-center">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Integrity</h3>
            <p className="text-base leading-relaxed">We are accountable to the communities we serve and the partners who invest in us. We uphold the highest standards of transparency, ethical conduct, and fiduciary responsibility in all our operations.</p>
          </div>
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg text-center">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Dignity</h3>
            <p className="text-base leading-relaxed">We treat the community we serve as partners. Our work upholds dignity of every person by fostering their capacity to make independent choices and actions.</p>
          </div>
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg text-center">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Human Rights</h3>
            <p className="text-base leading-relaxed">Our programmes integrate human right approach at all levels.</p>
          </div>
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg text-center">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Innovation</h3>
            <p className="text-base leading-relaxed">We are architects of a new paradigm, moving from creating dependency to building community-owned ecosystems. We constantly seek and implement the most effective, sustainable, and scalable solutions.</p>
          </div>
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg text-center">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Partnerships</h3>
            <p className="text-base leading-relaxed">We proactively forge strategic alliances with government, private sector actors, and civil society to leverage resources, share knowledge, and amplify our collective impact.</p>
          </div>
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg text-center">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Excellence</h3>
            <p className="text-base leading-relaxed">We deliver high-standard interventions, creating impactful and replicable models for community transformation.</p>
          </div>
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg text-center">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Stewardship</h3>
            <p className="text-base leading-relaxed">We manage entrusted resources responsibly, prioritizing environmental sustainability and community ownership.</p>
          </div>
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg text-center">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Faith-Inspired Hope</h3>
            <p className="text-base leading-relaxed">Guided by faith, we cultivate hope as a driver of change through compassion and shared purpose.</p>
          </div>
        </div>
      </div>
    </section>

    {/* Board of Directors - Updated Gilbert Mabingo profile per client feedback (Item 5) */}
    <section className="py-24 px-6 bg-ehh-navy/5">
      <div className="container mx-auto max-w-7xl">
        <h2 className="text-3xl md:text-5xl font-bold text-center mb-8">Board of Directors</h2>
        <p className="text-xl text-center mb-16 max-w-4xl mx-auto">
          Experienced leaders guiding our community-driven mission with expertise in diplomacy, health, development, governance, faith-based initiatives, and community empowerment.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-12 justify-items-center">
          {/* Amb. Pamela Chisanga */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm">
            <img 
              src="/team/pamela-chisanga.jpeg" 
              alt="Amb. Pamela Chisanga" 
              className="w-40 h-40 rounded-full mx-auto mb-6 object-cover shadow-lg" 
            />
            <h3 className="text-2xl font-semibold text-ehh-orange mb-2">Amb. Pamela Chisanga</h3>
            <p className="text-ehh-peach mb-4">Chairperson</p>
            <p className="text-base">A distinguished leader with over 20 years of executive experience in international development and strategic governance. Her distinguished career includes senior roles as Country Director for WaterAid and ActionAid, and High Commissioner Designate for Zambia. She brings deep expertise in national program strategy, policy advocacy, and multi-sector partnerships, with a focus on water security, sanitation, and child rights.</p>
          </div>

          {/* Bishop Ackwell Mbalamweshi */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm">
            <img 
              src="/team/ackwell.jpeg" 
              alt="Bishop Ackwell Mbalamweshi" 
              className="w-40 h-40 rounded-full mx-auto mb-6 object-cover shadow-lg" 
            />
            <h3 className="text-2xl font-semibold text-ehh-orange mb-2">Bishop Ackwell Mbalamweshi</h3>
            <p className="text-ehh-peach mb-4">Board Member</p>
            <p className="text-base">A passionate leader and founder of Emmanuel Fellowship Mission International (EFMI), focused on scalable community development. His extensive grassroots experience forms a robust foundation for this national vision. He is a strategic builder, having successfully grown a mission initiative, and brings deep expertise in local leadership training, program implementation, and community mobilization.</p>
          </div>

          {/* Mrs. Elizabeth Mbalamweshi */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm">
            <img 
              src="/team/mbalamweshi.jpeg" 
              alt="Mrs. Elizabeth Mbalamweshi" 
              className="w-40 h-40 rounded-full mx-auto mb-6 object-cover shadow-lg" 
            />
            <h3 className="text-2xl font-semibold text-ehh-orange mb-2">Mrs. Elizabeth Mbalamweshi</h3>
            <p className="text-ehh-peach mb-4">Board Member</p>
            <p className="text-base">A dedicated community advocate and counselor with a deep passion for women's empowerment and family support. Her expertise, gained through years of establishing grassroots initiatives, lies in designing programs that address psychosocial needs and build resilient community networks. Committed to creating safe spaces, she equips individuals with tools for growth and stability.</p>
          </div>

          {/* Dr. Matongo Shilimi */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm">
            <img 
              src="/team/matongo-shilimi.jpeg" 
              alt="Dr. Matongo Shilimi" 
              className="w-40 h-40 rounded-full mx-auto mb-6 object-cover shadow-lg" 
            />
            <h3 className="text-2xl font-semibold text-ehh-orange mb-2">Dr. Matongo Shilimi</h3>
            <p className="text-ehh-peach mb-4">Board Member</p>
            <p className="text-base">Dr. Matongo Shilimi has over 25 years of experience in the financial services sector specializing in banking and accounting. She has proven core competencies in Accounting and Auditing, Corporate and Financial Strategy Planning and Development, Financial Analysis and Reporting, Budget Development, Monitoring, and Analysis, Risk Management, Project Management, Business Research and Analysis, People Management and Staff Training among others.</p>
          </div>

          {/* Dr. Yumba Sanga Umbalo */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm">
            <img 
              src="/team/yumba-umbalo.jpeg" 
              alt="Dr. Yumba Sanga Umbalo" 
              className="w-40 h-40 rounded-full mx-auto mb-6 object-cover shadow-lg" 
            />
            <h3 className="text-2xl font-semibold text-ehh-orange mb-2">Dr. Yumba Sanga Umbalo</h3>
            <p className="text-ehh-peach mb-4">Board Member</p>
            <p className="text-base">A board-certified Naturopathic Doctor, Christian Counselor, and community development leader with over 20 years of experience in healthcare, entrepreneurship training, and holistic empowerment. He specializes in designing and implementing programs that integrate sustainable development, social justice, and compassionate care to transform communities.</p>
          </div>

          {/* Gilbert Mabingo - UPDATED: 30 → 25 years, Mabingo → Gilbert */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm">
            <img 
              src="/team/gilbert-mabingo.jpeg" 
              alt="Gilbert Mabingo" 
              className="w-40 h-40 rounded-full mx-auto mb-6 object-cover shadow-lg" 
            />
            <h3 className="text-2xl font-semibold text-ehh-orange mb-2">Gilbert Mabingo</h3>
            <p className="text-ehh-peach mb-4">Board Member</p>
            <p className="text-base">Gilbert is a visionary and transformational leader with over 25 years of experience driving growth in financial services, telecommunications, security, and management consulting. He has a proven record of delivering significant results, earning prestigious accolades. An expert in strategic leadership, market expansion, and operational excellence.</p>
          </div>

          {/* Philemon Masangula */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm">
            <img 
              src="/team/philemon-masangula.jpeg" 
              alt="Philemon Masangula" 
              className="w-40 h-40 rounded-full mx-auto mb-6 object-cover shadow-lg" 
            />
            <h3 className="text-2xl font-semibold text-ehh-orange mb-2">Philemon Masangula</h3>
            <p className="text-ehh-peach mb-4">Board Member</p>
            <p className="text-base">A distinguished WASH professional with over 20 years of experience across urban, peri-urban, and rural Zambia. He currently heads Monitoring and Evaluation (M&E) at Lusaka Water Supply and Sanitation Co. His expertise uniquely bridges engineering, strategic planning, and community engagement.</p>
          </div>

          {/* Nsabila Mabingo */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm">
            <img 
              src="/team/nsabila-mabingo.jpeg" 
              alt="Nsabila Mabingo" 
              className="w-40 h-40 rounded-full mx-auto mb-6 object-cover shadow-lg" 
            />
            <h3 className="text-2xl font-semibold text-ehh-orange mb-2">Nsabila Mabingo</h3>
            <p className="text-ehh-peach mb-4">Board Member</p>
            <p className="text-base">A results-driven banking and administration professional with over 22 years of expertise in retail and corporate banking, customer service, and relationship management. Proven ability to drive significant business growth, enhance customer satisfaction, and lead high-performing teams.</p>
          </div>

          {/* Matimba Chikuba */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm">
            <img 
              src="/team/matimba-chikuba.jpeg" 
              alt="Matimba Chikuba" 
              className="w-40 h-40 rounded-full mx-auto mb-6 object-cover shadow-lg" 
            />
            <h3 className="text-2xl font-semibold text-ehh-orange mb-2">Matimba Chikuba</h3>
            <p className="text-ehh-peach mb-4">Board Member</p>
            <p className="text-base">A seasoned financial professional based in Lusaka, with extensive leadership experience in the banking sector. She has built her expertise in banking operations through key roles at Cavmont Bank and African Banking Corporation, before advancing to her current position as Head of Distribution Channels at a major financial institution.</p>
          </div>

          {/* Machona Kasambala */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm">
            <img 
              src="/team/machona-kasambala.jpeg" 
              alt="Machona Kasambala" 
              className="w-40 h-40 rounded-full mx-auto mb-6 object-cover shadow-lg" 
            />
            <h3 className="text-2xl font-semibold text-ehh-orange mb-2">Machona Kasambala</h3>
            <p className="text-ehh-peach mb-4">Board Member</p>
            <p className="text-base">An accomplished communications specialist and Programme Editor at the National Agricultural Information Services, with extensive experience in agricultural journalism and public information. He excels at translating complex agricultural policies and national development goals into clear, impactful public messaging.</p>
          </div>
        </div>
      </div>
    </section>

    {/* Operational Objectives - Fixed typo in heading */}
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl md:text-5xl font-bold text-center mb-16">Our Operational Objectives</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Secure Foundational Well-being</h3>
            <p className="text-base leading-relaxed">To ensure vulnerable communities have access to essential needs, including healthcare, nutrition, and sustainable livelihoods, establishing a foundation for a dignified life.</p>
          </div>
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Empower for Sustainable Resilience</h3>
            <p className="text-base leading-relaxed">To equip individuals and families with the knowledge, skills, and resources for economic self-reliance and environmentally sound practices, breaking the cycle of poverty.</p>
          </div>
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Catalyse Community-Led Change</h3>
            <p className="text-base leading-relaxed">To build the capacity of communities and families to advocate for their rights, demand accountability from leaders, and directly influence inclusive and equitable policies.</p>
          </div>
          <div className="bg-ehh-cream rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Forge Strategic Partnerships</h3>
            <p className="text-base leading-relaxed">To network and collaborate with government, civil society, and development actors to amplify impact, share knowledge, and ensure programmes sustainability.</p>
          </div>
        </div>
      </div>
    </section>

    {/* Framework for Accountability and Learning - New section from PDF */}
    <section className="py-24 px-6 bg-ehh-navy/5">
      <div className="container mx-auto max-w-6xl text-center">
        <h2 className="text-3xl md:text-5xl font-bold mb-10">Our Framework for Accountability and Learning</h2>
        <p className="text-lg md:text-xl leading-relaxed max-w-4xl mx-auto mb-12">
          To ensure this paradigm shift becomes a lasting reality, our Monitoring, Evaluation, and Learning (MEL) framework is built on transparency, adaptation, and community voice. It is designed not just to prove results, but to improve them.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="bg-white rounded-2xl p-8 shadow-lg text-left">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Data-Driven Insight</h3>
            <p className="text-base leading-relaxed">We will move beyond simple activity reports to track leading indicators of systemic change not just "number of youths trained," but "income increase of graduates" and "number of new businesses launched."</p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-lg text-left">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Community-Led Evaluation</h3>
            <p className="text-base leading-relaxed">Our programmes valuation will be a collaborative "Learning Summit" where project staff, donor partners, and most critically community representatives jointly assess impact, identify lessons, and shape the next phase of investment.</p>
          </div>
        </div>
      </div>
    </section>

    {/* Clarion Call - New section from PDF */}
    <section className="py-24 px-6 bg-white text-center">
      <div className="container mx-auto max-w-5xl">
        <h2 className="text-3xl md:text-5xl font-bold mb-10">EMA House of Hope Clarion Call: A New Paradigm for Community Transformation</h2>
        <p className="text-lg md:text-xl leading-relaxed max-w-4xl mx-auto mb-12">
          The challenges facing our community are interconnected poverty, ill health, and unemployment form a self-reinforcing cycle. Traditional, siloed approaches provide temporary relief but fail to break the cycle.
        </p>
        <p className="text-lg md:text-xl leading-relaxed max-w-4xl mx-auto">
          EMA House of Hope is launching a fundamental paradigm shift moving from a model of dependence to one of ecosystem empowerment. Our integrated approach does not just deliver services; it co-creates a new reality where the community becomes the primary agent of its own transformation.
        </p>
      </div>
    </section>

    {/* Call to Action */}
    <section className="py-24 px-6 text-center bg-ehh-orange/10">
      <div className="container mx-auto max-w-5xl">
        <h2 className="text-3xl md:text-5xl font-bold mb-8">Partner With Us</h2>
        <p className="text-xl mb-12 max-w-3xl mx-auto">
          Together, we synchronise interventions to uplift communities and secure sustainable futures across generations.
        </p>
        <Link 
          to="/donate" 
          className="inline-block bg-ehh-orange text-ehh-cream px-12 py-5 rounded-lg text-xl font-semibold hover:bg-ehh-coral transition-shadow shadow-xl"
        >
          Support Our Mission
        </Link>
      </div>
    </section>
  </div>
);

export default About;